import { Link, useSearch } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useVerifyEmail } from "@/features/auth/api/useAuth";
import { useApiErrorMessage } from "@/features/auth/lib/useApiErrorMessage";
import { Alert } from "@/shared/ui/Alert";
import { Card } from "@/shared/ui/Card";
import { Skeleton } from "@/shared/ui/Skeleton";

export function VerifyEmailPage() {
  const { t } = useTranslation("auth");
  const { token } = useSearch({ from: "/public/verify-email" });
  const verify = useVerifyEmail();
  const errorMessage = useApiErrorMessage();
  const consumed = useRef(false);

  useEffect(() => {
    // Le jeton est à usage unique : un double montage (mode strict de React)
    // consommerait le lien puis afficherait « lien invalide » à l'utilisateur.
    if (consumed.current || token === undefined) {
      return;
    }
    consumed.current = true;
    verify.mutate(token);
  }, [token, verify]);

  return (
    <Card className="mx-auto max-w-md">
      <h1 className="mb-4 text-2xl font-bold tracking-tight">{t("verifyEmail.heading")}</h1>

      {token === undefined && <Alert tone="error">{t("verifyEmail.missingToken")}</Alert>}

      {verify.isPending && <Skeleton className="h-4 w-2/3" label={t("verifyEmail.pending")} />}

      {verify.isSuccess && (
        <>
          <Alert tone="success">{t("verifyEmail.success")}</Alert>
          <p className="mt-6 text-sm">
            <Link to="/login" className="text-brand-600 hover:underline">
              {t("verifyEmail.goToLogin")}
            </Link>
          </p>
        </>
      )}

      {verify.isError && (
        <>
          <Alert tone="error">{errorMessage(verify.error)}</Alert>
          <p className="mt-6 text-sm text-slate-600 dark:text-slate-400">
            {t("verifyEmail.expiredHelp")}
          </p>
        </>
      )}
    </Card>
  );
}
